class MegaMenu extends HTMLElement {
  connectedCallback() {
    this.closeTimeout = null;
    this.isOpen = false;
    this.previousOverflow = '';
    this.triggers = document.querySelectorAll(`[data-mega-menu-trigger="${this.id}"]`);
    this.productsList = this.querySelector('.mega-menu__products-list');
    this.productItems = Array.from(this.querySelectorAll('.mega-menu__product-item'));

    if (!this.id || this.triggers.length === 0) return;

    this.open = this.open.bind(this);
    this.close = this.close.bind(this);
    this.scheduleClose = this.scheduleClose.bind(this);
    this.cancelClose = this.cancelClose.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
    this.onProductsPointerDown = this.onProductsPointerDown.bind(this);
    this.onProductsPointerMove = this.onProductsPointerMove.bind(this);
    this.onProductsPointerEnd = this.onProductsPointerEnd.bind(this);
    this.onProductsClick = this.onProductsClick.bind(this);
    this.preventProductsNativeDrag = this.preventProductsNativeDrag.bind(this);

    this.triggers.forEach((trigger) => {
      trigger.classList.add('has-mega-menu');
      trigger.addEventListener('mouseenter', this.open);
      trigger.addEventListener('focus', this.open);
      trigger.addEventListener('mouseleave', this.scheduleClose);
      trigger.addEventListener('focusout', this.scheduleClose);
      trigger.setAttribute('aria-controls', this.id);
      trigger.setAttribute('aria-expanded', 'false');
    });

    this.addEventListener('mouseenter', this.cancelClose);
    this.addEventListener('mouseleave', this.scheduleClose);
    this.addEventListener('focusin', this.open);
    this.addEventListener('focusout', this.scheduleClose);
    this.productsList?.addEventListener('pointerdown', this.onProductsPointerDown);
    this.productsList?.addEventListener('pointermove', this.onProductsPointerMove);
    this.productsList?.addEventListener('pointerup', this.onProductsPointerEnd);
    this.productsList?.addEventListener('pointercancel', this.onProductsPointerEnd);
    this.productsList?.addEventListener('lostpointercapture', this.onProductsPointerEnd);
    this.productsList?.addEventListener('click', this.onProductsClick);
    this.productsList?.addEventListener('dragstart', this.preventProductsNativeDrag);
    document.addEventListener('keydown', this.onKeyDown);
  }

  disconnectedCallback() {
    if (!this.triggers || this.triggers.length === 0) return;

    this.close();

    this.triggers?.forEach((trigger) => {
      trigger.removeEventListener('mouseenter', this.open);
      trigger.removeEventListener('focus', this.open);
      trigger.removeEventListener('mouseleave', this.scheduleClose);
      trigger.removeEventListener('focusout', this.scheduleClose);
      trigger.classList.remove('has-mega-menu', 'is-active');
      trigger.removeAttribute('aria-controls');
      trigger.removeAttribute('aria-expanded');
    });

    this.removeEventListener('mouseenter', this.cancelClose);
    this.removeEventListener('mouseleave', this.scheduleClose);
    this.removeEventListener('focusin', this.open);
    this.removeEventListener('focusout', this.scheduleClose);
    this.productsList?.removeEventListener('pointerdown', this.onProductsPointerDown);
    this.productsList?.removeEventListener('pointermove', this.onProductsPointerMove);
    this.productsList?.removeEventListener('pointerup', this.onProductsPointerEnd);
    this.productsList?.removeEventListener('pointercancel', this.onProductsPointerEnd);
    this.productsList?.removeEventListener('lostpointercapture', this.onProductsPointerEnd);
    this.productsList?.removeEventListener('click', this.onProductsClick);
    this.productsList?.removeEventListener('dragstart', this.preventProductsNativeDrag);
    window.clearTimeout(this.productsClickResetTimeout);
    window.clearTimeout(this.productsSettleTimeout);
    document.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('scroll', this.close);
  }

  open() {
    this.cancelClose();

    if (this.isOpen) return;

    document.querySelectorAll('mega-menu.is-open').forEach((menu) => {
      if (menu !== this) menu.close();
    });

    this.isOpen = true;
    this.classList.add('is-open');
    this.setAttribute('aria-hidden', 'false');
    this.triggers.forEach((trigger) => {
      trigger.classList.add('is-active');
      trigger.setAttribute('aria-expanded', 'true');
    });

    window.addEventListener('scroll', this.close, { passive: true });
  }

  scheduleClose() {
    this.cancelClose();
    this.closeTimeout = window.setTimeout(() => this.close(), 140);
  }

  cancelClose() {
    if (!this.closeTimeout) return;

    window.clearTimeout(this.closeTimeout);
    this.closeTimeout = null;
  }

  close() {
    this.cancelClose();

    if (!this.isOpen) return;

    this.isOpen = false;
    this.classList.remove('is-open');
    this.setAttribute('aria-hidden', 'true');
    this.triggers?.forEach((trigger) => {
      trigger.classList.remove('is-active');
      trigger.setAttribute('aria-expanded', 'false');
    });

    window.removeEventListener('scroll', this.close);
  }

  onKeyDown(event) {
    if (event.key !== 'Escape') return;

    this.close();
  }

  onProductsPointerDown(event) {
    if (
      event.pointerType === 'touch' ||
      event.button !== 0 ||
      !event.isPrimary ||
      this.productsList.scrollWidth <= this.productsList.clientWidth
    ) {
      return;
    }

    this.productsPointerId = event.pointerId;
    this.productsPointerStartX = event.clientX;
    this.productsPointerStartY = event.clientY;
    this.productsScrollStartX = this.productsList.scrollLeft;
    this.isProductsDragging = false;
  }

  onProductsPointerMove(event) {
    if (event.pointerId !== this.productsPointerId) return;

    const distanceX = event.clientX - this.productsPointerStartX;
    const distanceY = event.clientY - this.productsPointerStartY;

    if (!this.isProductsDragging) {
      if (Math.abs(distanceX) < 6 || Math.abs(distanceX) <= Math.abs(distanceY)) return;

      this.isProductsDragging = true;
      this.preventProductsClick = true;
      this.productsList.classList.add('is-dragging');
      this.productsList.setPointerCapture(event.pointerId);
    }

    event.preventDefault();
    this.productsList.scrollLeft = this.productsScrollStartX - distanceX;
  }

  onProductsPointerEnd(event) {
    if (event.pointerId !== this.productsPointerId) return;

    const wasDragging = this.isProductsDragging;

    this.productsPointerId = null;
    this.isProductsDragging = false;

    if (!wasDragging) return;

    if (this.productsList.hasPointerCapture(event.pointerId)) {
      this.productsList.releasePointerCapture(event.pointerId);
    }

    this.settleProductsList();
    window.clearTimeout(this.productsClickResetTimeout);
    this.productsClickResetTimeout = window.setTimeout(() => {
      this.preventProductsClick = false;
    }, 0);
  }

  onProductsClick(event) {
    if (!this.preventProductsClick) return;

    event.preventDefault();
    this.preventProductsClick = false;
    window.clearTimeout(this.productsClickResetTimeout);
  }

  preventProductsNativeDrag(event) {
    event.preventDefault();
  }

  get maxProductsScrollLeft() {
    return Math.max(0, this.productsList.scrollWidth - this.productsList.clientWidth);
  }

  getProductScrollLeft(item) {
    const listLeft = this.productsList.getBoundingClientRect().left;
    const itemLeft = item.getBoundingClientRect().left - listLeft;

    return Math.max(
      0,
      Math.min(this.productsList.scrollLeft + itemLeft, this.maxProductsScrollLeft)
    );
  }

  settleProductsList() {
    if (this.productItems.length === 0) {
      this.productsList.classList.remove('is-dragging');
      return;
    }

    const scrollLeft = this.productsList.scrollLeft;
    const closestItem = this.productItems.reduce((closest, item) => {
      return Math.abs(scrollLeft - this.getProductScrollLeft(item)) <
        Math.abs(scrollLeft - this.getProductScrollLeft(closest))
        ? item
        : closest;
    });
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.productsList.classList.add('is-settling');
    this.productsList.classList.remove('is-dragging');
    this.productsList.scrollTo({
      left: this.getProductScrollLeft(closestItem),
      behavior: reducedMotion ? 'auto' : 'smooth',
    });

    window.clearTimeout(this.productsSettleTimeout);
    this.productsSettleTimeout = window.setTimeout(() => {
      this.productsList.classList.remove('is-settling');
    }, reducedMotion ? 0 : 400);
  }
}

if (!customElements.get('mega-menu')) {
  customElements.define('mega-menu', MegaMenu);
}
